import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { AppContext } from 'src/components/app-context';
import AdContainer from '../../ads';
import { useAdServiceContext } from '../../ads/services/context';
import type { AdPlacement } from '../../ads/types';
import * as S from './styles';
import { WideContext } from 'src/contexts/wide-context';
import { BleedContext } from './context';

const topAdSpacing = { top: '10px' };
const bottomAdSpacing = { bottom: '10px' };

type Props = {
  className?: string;
  children: React.ReactNode;
  topPlacements?: PlacementType[];
  bottomPlacements?: PlacementType[];
  withAds?: boolean;
  adsDelayed?: object;
  wideContent?: boolean;
  'data-test'?: string;
  alignLeft?: boolean;
};

type PlacementType = {
  placement: AdPlacement;
  min: number;
  max: number;
};

export type TakeoverSettings = {
  width: number;
  height: number;
  offgrid: boolean;
  topMargin: number;
};

export type AdServiceContextT = {
  takeover?: TakeoverSettings;
};

const PageContent = (props: Props) => {
  const {
    className,
    children,
    adsDelayed = {},
    alignLeft = false,
    wideContent = false,
    withAds = false,
    topPlacements = [
      {
        scalable: true,
        formats: ['toppanorama_wde' as const, 'wallpaper' as const],
        placement: 'panorama_top' as const,
        min: 861,
        max: 10000,
      },
    ],
    bottomPlacements = [
      {
        placement: 'panorama_footer' as const,
        format: 'panorama_4_wde' as const,
        min: 1024,
        max: 10000,
        triggerPoint: 100,
      },
      {
        placement: 'dubbelpanorama3' as const,
        format: 'panorama_3' as const,
        min: 0,
        max: 860,
        triggerPoint: 100,
      },
    ],
  } = props;

  const adServiceContext: AdServiceContextT | null = useAdServiceContext();
  const { market } = useContext(AppContext);
  const { isWide } = useContext(WideContext);
  const adsActive = market && market.ads && market.ads.active;
  const showAds = withAds && adsActive;
  const takeover =
    showAds && adServiceContext && adServiceContext.takeover
      ? adServiceContext.takeover
      : undefined;

  const [ref, setRef] = useState<HTMLDivElement | null>(null);
  const [bleed, setBleed] = useState({ left: 0, right: 0 });
  const updateBleed = useCallback(() => {
    setTimeout(
      () => {
        const rect = ref?.getBoundingClientRect() ?? {
          left: 0,
          right: 0,
        };

        setBleed({
          left: -rect.left,
          right: -document.documentElement.clientWidth + rect.right,
        });
      },
      // there is a transition on the page content that we have
      // to wait for before calculating the new bleed.
      300
    );
  }, [ref]);
  useEffect(() => {
    window.addEventListener('resize', updateBleed, { passive: true });

    updateBleed();

    return () => {
      window.removeEventListener('resize', updateBleed);
    };
  }, [updateBleed]);

  const contextValue = useMemo(() => ({ bleed }), [bleed]);

  return (
    <S.PageContent
      data-test={props['data-test']}
      className={className}
      isWide={isWide}
    >
      <S.Wrapper>
        {showAds && topPlacements.length > 0 && (
          <S.TopAds takeover={takeover} className="top-ads">
            {topPlacements.map((adProps, index) => {
              return (
                <AdContainer
                  {...adProps}
                  key={`${adProps.placement}-${index.toString()}`}
                  isReady={adsDelayed[adProps.placement]}
                  spacing={takeover ? {} : topAdSpacing}
                />
              );
            })}
          </S.TopAds>
        )}
        <BleedContext.Provider value={contextValue}>
          <S.Content
            ref={setRef}
            takeover={takeover}
            hasAds={adsActive}
            wideContent={wideContent}
            alignLeft={alignLeft}
            isWide={isWide}
          >
            {children}
          </S.Content>
        </BleedContext.Provider>

        {showAds && bottomPlacements.length > 0 && (
          <aside className="bottom-ads">
            {bottomPlacements.map(adProps => (
              <AdContainer
                {...adProps}
                key={adProps.placement}
                isReady={adsDelayed[adProps.placement]}
                spacing={bottomAdSpacing}
              />
            ))}
          </aside>
        )}
      </S.Wrapper>

      {showAds && (
        <S.OutsiderAds takeover={takeover} className="outsider-ads">
          <S.SkyscraperAd
            key="skyscraper_1"
            placement="skyscraper_1"
            format="widescreen_1"
            min={1199}
            max={10000}
            scalable
            scaleBase=".outsider-ads"
          />
          <S.SkyscraperAd
            key="skyscraper_2"
            placement="skyscraper_2"
            format="widescreen_2"
            min={1199}
            max={10000}
            scalable
            scaleBase=".outsider-ads"
          />
          <S.SkyscraperAd
            key="skyscraper_3"
            placement="skyscraper_3"
            format="widescreen_3"
            min={1199}
            max={10000}
            scalable
            scaleBase=".outsider-ads"
          />
        </S.OutsiderAds>
      )}
    </S.PageContent>
  );
};

export default memo(PageContent);
